import type { ItemNode } from "../parser/types";
import type { BlankFieldType } from "./types";

/**
 * How a blank's kind is decided, as an ordered table of named rules.
 *
 * This used to be a run of `if` statements inside `inferType`, and three
 * things were wrong with it.
 *
 * Every test but one read `item.displayText`, the whole line, so a single
 * match anywhere on a line typed every blank on that line the same way.
 * Ruling 52 of 7 October 2026 then showed that the width test added to fix
 * that is wrong in both directions on the trainee's own warning order:
 * "Grid _________ @ ______ hrs." typed the time as a grid because the blank
 * is six wide, and "Sqn Crash RV grid 19T ___________" typed a grid as a
 * value because the zone designator sits between "grid" and an eleven-wide
 * blank. **Width is evidence, not a verdict.**
 *
 * And nothing recorded which rule fired, so a wrong answer could only be
 * diagnosed by reading the source. Ruling 50 requires every classification
 * decision to log the blank, its width, its line, the rule that fired and
 * that rule's reason, so each rule now carries its own name and reason.
 *
 * The order below is the order they are tested, and the first to fire wins.
 */
export interface BlankTypingContext {
  /** The text after the blank, to the end of its line. */
  readonly after: string;
  /** The text before the blank, from the start of its line. */
  readonly before: string;
  readonly item: ItemNode;
  /** The blank's source token, `_9C` or a run of underscores. */
  readonly token: string;
  /** Characters wide. `_9C` counts as one. */
  readonly width: number;
}

export interface BlankTypingRule {
  readonly fires: (context: BlankTypingContext) => boolean;
  /** Kebab-case, stable: it is written to the diagnostic log. */
  readonly name: string;
  /** Why this rule decides what it decides. Logged with the decision. */
  readonly reason: string;
  readonly type: BlankFieldType;
}

/**
 * Every grid is six figures, by the ruling of 12 September 2026. A six-wide
 * blank is therefore a grid **when nothing nearer says otherwise**, which is
 * why this rule sits below the context rules rather than above them.
 */
const GRID_WIDTH = 6;

/**
 * Words that make the blank before them a time.
 *
 * `@` is deliberately absent. On "Grid ____ @ ____ hrs." the `@` follows the
 * grid and introduces the time that comes after it, so reading `@` as a
 * marker for the blank before it typed the grid as a time. `@` is a marker
 * for the blank that *follows* it, which is the `time-marker-precedes` rule.
 */
// Ruling 52 named "hrs", "hr" and "a time word". `h` and `Z` were
// neither named nor ever observed: across 282 corpus blanks all 12
// firings matched "hrs". Removed by step 1d of 8 October 2026 (c)
// rather than kept on the strength of a guess about Zulu time.
const TIME_AFTER = /^\s*(?:hrs?\b|hours?\b)/i;

/** A grid zone designator, such as `19T`, may sit between "grid" and the blank. */
const GRID_BEFORE = /\bgrid\s*(?:[0-9]{1,2}[A-Z]\s*)?$/i;

/** Ruling 53: a squadron designator immediately precedes "Sqn". */
const SQUADRON_AFTER = /^\s*Sqn\b/i;

/**
 * Ruling 54: `F1 ____ F2 ____` is two frequencies. The referent vocabulary
 * already carries `frequency` as a List B UNGROUNDED term with the patterns
 * "freq" and "frequency"; `F1` and `F2` are added by that ruling and not by
 * an implementer, and the term stays UNGROUNDED until it is sourced.
 */
const FREQUENCY_BEFORE = /\b(?:F\s?[0-9]|freq(?:uency)?)\s*[:.]?\s*$/i;

/** Ruling 54, from the vocabulary's own patterns for `notice-to-move`. */
const NTM_AFTER =
  /^\s*(?:mins?\b|minutes?\b)?\s*(?:NTM\b|notice\s+to\s+move\b)/i;

/** Ruling 54, from the vocabulary's own patterns for `h-hour`. */
const H_HOUR_BEFORE = /\b(?:H[-\s]?Hr|H[-\s]?Hour)\s*(?:is|=|:)?\s*$/i;

/** Doc 03 section 4.5 and the vocabulary's `callsign` term. */
const CALLSIGN_BEFORE = /\b(?:callsign|c\/s)\s*[:.]?\s*$/i;

export const BLANK_TYPING_RULES: readonly BlankTypingRule[] = [
  {
    name: "callsign-prefix-token",
    reason: "the _9C token is a callsign prefix by the R1 lexicon",
    type: "callsign-prefix",
    fires: ({ token }) => token === "_9C",
  },
  {
    name: "time-word-follows",
    reason:
      "Ruling 52a: a blank followed by hrs, hours or @ is a time, never a grid, whatever its width",
    type: "time",
    fires: ({ after }) => TIME_AFTER.test(after),
  },
  {
    name: "time-marker-precedes",
    reason:
      "Ruling 52a: a blank introduced by @ states a time, which is why @ after a blank does not make that blank one",
    type: "time",
    fires: ({ before }) => /@\s*$/.test(before),
  },
  {
    name: "notice-to-move-follows",
    reason:
      "Ruling 54: the vocabulary carries notice-to-move; a blank followed by mins NTM is one",
    type: "notice-to-move",
    fires: ({ after }) => NTM_AFTER.test(after),
  },
  {
    name: "grid-label-precedes",
    reason:
      "Ruling 52b: a blank preceded by grid, with or without a zone designator between, is a grid at any width",
    type: "grid",
    fires: ({ before }) => GRID_BEFORE.test(before),
  },
  {
    name: "squadron-designator-precedes",
    reason:
      "Rulings 53 and 56: a blank immediately preceding Sqn is a squadron designator, which is its own kind and not a callsign, wherever the construction appears",
    type: "squadron-designator",
    fires: ({ after }) => SQUADRON_AFTER.test(after),
  },
  {
    name: "frequency-label-precedes",
    reason:
      "Ruling 54: the vocabulary carries frequency; F1, F2 and freq all label one",
    type: "frequency",
    fires: ({ before }) => FREQUENCY_BEFORE.test(before),
  },
  {
    name: "h-hour-label-precedes",
    reason: "Ruling 54: the vocabulary carries h-hour",
    type: "h-hour",
    fires: ({ before }) => H_HOUR_BEFORE.test(before),
  },
  {
    name: "callsign-label-precedes",
    reason:
      "Ruling 54: the vocabulary carries callsign, labelled callsign or c/s",
    type: "callsign",
    fires: ({ before }) => CALLSIGN_BEFORE.test(before),
  },
  {
    name: "groupings-and-tasks-line-head",
    reason:
      "Ruling 40, applied wherever the construction appears by Ruling 53: a blank opening a Groupings and Tasks line before a colon is the owning callsign",
    type: "callsign",
    fires: ({ after, before, item }) =>
      before.trim() === "" && /^\s*:/.test(after) && item.id.includes("/GT/"),
  },
  {
    name: "chain-of-command-line",
    reason:
      "doc 03 section 4.5: every blank on a chain of command line is a callsign",
    type: "callsign",
    fires: ({ item }) => /chain of command|\bCoC\b/i.test(item.displayText),
  },
  {
    name: "table-row-cell",
    reason: "a blank cell in a timings table is a time",
    type: "time",
    fires: ({ item }) => item.type === "table",
  },
  {
    name: "six-figure-width",
    reason:
      "every grid is six figures by the ruling of 12 September 2026, and nothing nearer this blank says otherwise",
    type: "grid",
    fires: ({ width }) => width === GRID_WIDTH,
  },
  {
    name: "control-measure-precedes",
    reason:
      "a blank immediately preceded by a start point or line of departure names that control measure",
    type: "control-measure",
    fires: ({ before }) => /\b(?:SP|LD)\s*$/i.test(before),
  },
  {
    name: "formation-up-follows",
    reason: "a blank followed by Up states a formation",
    type: "formation",
    fires: ({ after }) => /^\s*Up\b/i.test(after),
  },
];

export interface BlankTypingDecision {
  readonly reason: string;
  readonly rule: string;
  readonly type: BlankFieldType;
}

/**
 * The fallback, when no rule fires. `unknown` is the classifier's own word
 * for itself and is never shown to the trainee as an instruction.
 */
export const NO_RULE_FIRED: BlankTypingDecision = {
  reason: "no rule fired; the kind of value is not stated near this blank",
  rule: "none",
  type: "unknown",
};

/** The first rule to fire, with its name and reason for the log. */
export function decideBlankType(
  context: BlankTypingContext,
): BlankTypingDecision {
  const rule = BLANK_TYPING_RULES.find(({ fires }) => fires(context));
  if (rule === undefined) return NO_RULE_FIRED;
  return { reason: rule.reason, rule: rule.name, type: rule.type };
}
